import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { RootState } from "features/config/store";
import { BatchProductReviewsParam } from "features/stock-ordering/core/stock-ordering.params";
import {
  GetBatchProductReviewsRepository,
  BatchProductReviewsResponse,
} from "features/stock-ordering/data/stock-ordering.repository";
import { ProductReviewsData } from "./product-reviews.slice";

export enum BatchProductReviewsState {
  initial,
  inProgress,
  success,
  fail,
}

export interface BatchProductReviewsInitialState {
  status: BatchProductReviewsState;
  message: string;
  data: { [productId: string]: ProductReviewsData } | undefined;
}

const initialState: BatchProductReviewsInitialState = {
  status: BatchProductReviewsState.initial,
  message: "",
  data: undefined,
};

export const getBatchProductReviews = createAsyncThunk(
  "getBatchProductReviews",
  async (productIds: string[], { rejectWithValue }) => {
    try {
      const param: BatchProductReviewsParam = { productIds };
      const response: BatchProductReviewsResponse =
        await GetBatchProductReviewsRepository(param);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (!error.response) {
          throw error;
        }
        throw rejectWithValue(error.response.data.message);
      }
    }
  }
);

export const batchProductReviewsSlice = createSlice({
  name: "batchProductReviews",
  initialState,
  reducers: {
    resetBatchProductReviews: (state) => {
      state.status = BatchProductReviewsState.initial;
      state.message = "Reset request";
      state.data = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBatchProductReviews.pending, (state) => {
        state.status = BatchProductReviewsState.inProgress;
      })
      .addCase(getBatchProductReviews.fulfilled, (state, action) => {
        if (action.payload) {
          state.status = BatchProductReviewsState.success;
          state.data = action.payload;
          state.message = "Batch reviews loaded successfully";
        }
      })
      .addCase(getBatchProductReviews.rejected, (state, action) => {
        state.status = BatchProductReviewsState.fail;
        state.message = action.payload as string;
        state.data = undefined;
      });
  },
});

export const selectBatchProductReviews = (state: RootState) =>
  state.batchProductReviews;

export const { resetBatchProductReviews } = batchProductReviewsSlice.actions;
export default batchProductReviewsSlice.reducer;
